import type { Collaborator, PreferredDesigner } from "@nlc/shared-types";

import { useRef } from "react";
import { useListData } from "react-stately";

import { Avatar } from "@/components/avatar";
import { Description, Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Tag } from "@/components/tag-group";
import { useGetPreferredDesigners } from "@/features/request/shared/UseGetPreferredDesigners";
import { useGetProjectCollaborators } from "@/features/request/shared/UseGetProjectCollaborators";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { useMbcRequestSettings } from "../hooks/useMbcRequestSettings";
import { SidebarCard } from "./SidebarCard";

type PeopleCardProps = {
  requestId: null | number;
};

type PersonOption = Collaborator | PreferredDesigner;

type PeopleSelectProps = {
  description: string;
  emptyText: string;
  items: PersonOption[];
  label: string;
  onChange: (ids: number[]) => void;
  placeholder: string;
  selectedIds: number[];
};

function PeopleSelect({
  description,
  emptyText,
  items,
  label,
  onChange,
  placeholder,
  selectedIds,
}: PeopleSelectProps) {
  const currentIds = useRef(selectedIds);
  const selectedList = useListData<PersonOption>({
    getKey: (item) => item.id,
    initialItems: items.filter((item) => selectedIds.includes(Number(item.id))),
  });

  const update = (ids: number[]) => {
    currentIds.current = ids;
    onChange(ids);
  };

  return (
    <MultiSelectField>
      <Label>{label}</Label>
      <MultiSelect
        items={items}
        onItemAdd={(key) => update([...currentIds.current, Number(key)])}
        onItemRemove={(key) =>
          update(currentIds.current.filter((id) => id !== Number(key)))
        }
        placeholder={placeholder}
        renderEmptyState={(inputValue) => (
          <span className="p-2 text-text-primary">
            {inputValue ? `No results found for: ${inputValue}` : emptyText}
          </span>
        )}
        selectedList={selectedList}
        tag={(item) => (
          <Tag textValue={String(item.id)}>
            <Avatar
              alt={item.textValue}
              className="size-3 rounded-full"
              src={item.profilePicture}
            />
            {item.textValue}
          </Tag>
        )}
      >
        {(item) => (
          <MultiSelectItem textValue={item.textValue}>
            {item.textValue}
          </MultiSelectItem>
        )}
      </MultiSelect>
      <Description>{description}</Description>
    </MultiSelectField>
  );
}

export function PeopleCard({ requestId }: PeopleCardProps) {
  const settings = useMbcRequestSettings(requestId);
  const currentProjectId = useGetCurrentProjectId();
  const projectId = settings.projectId ?? currentProjectId;
  const { data: designers } = useGetPreferredDesigners(projectId);
  const { data: collaborators } = useGetProjectCollaborators(projectId);

  return (
    <SidebarCard>
      <h2 className="text-lg font-bold text-text-primary">People</h2>
      <div className="mt-4 flex flex-col gap-5">
        {designers && !settings.isLoading ? (
          <PeopleSelect
            description="Choose from the designers assigned to your account."
            emptyText="No designers available"
            items={designers}
            label="Preferred designers"
            onChange={(ids) => settings.save({ preferredDesignerIds: ids })}
            placeholder="Select from your designers"
            selectedIds={settings.preferredDesignerIds}
          />
        ) : null}
        {collaborators && !settings.isLoading ? (
          <PeopleSelect
            description="They can view this brief and get notified when someone comments."
            emptyText="No collaborators available"
            items={collaborators}
            label="Collaborators"
            onChange={(ids) => settings.save({ collaboratorIds: ids })}
            placeholder="Search your users"
            selectedIds={settings.collaboratorIds}
          />
        ) : null}
      </div>
    </SidebarCard>
  );
}
